package com.qrkontaq.ui.tenant

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.qrkontaq.data.dto.PublicRestaurantDto
import com.qrkontaq.data.dto.formatMoneyMinor
import com.qrkontaq.ui.theme.Brand
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.browser.document
import kotlinx.coroutines.await
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div
import org.w3c.dom.HTMLScriptElement
import kotlin.js.Date
import kotlin.js.Promise

/** Tenant strings for the per-restaurant menu language (see MenuI18n). */
internal fun tenantStrings(lang: String): MenuStrings = menuT(lang)

// ---------- qrcode-generator (MIT) lazy loading ----------

private const val QR_LIB_URL = "https://cdn.jsdelivr.net/npm/qrcode-generator@1.4.4/qrcode.min.js"

private var qrLibPromise: Promise<Boolean>? = null

private fun qrLibAvailable(): Boolean =
    js("typeof window.qrcode === 'function'") as Boolean

/** Loads the QR script from CDN once; resolves false on network failure. */
private fun loadQrLib(): Promise<Boolean> {
    if (qrLibAvailable()) return Promise.resolve(true)
    qrLibPromise?.let { return it }
    val promise =
        Promise<Boolean> { resolve, _ ->
            val script = document.createElement("script") as HTMLScriptElement
            script.src = QR_LIB_URL
            script.addEventListener("load", { resolve(qrLibAvailable()) })
            script.addEventListener("error", { resolve(false) })
            document.head?.appendChild(script)
        }
    qrLibPromise = promise
    return promise
}

/** SVG markup for [text], or null when the library is missing or failed. */
private fun qrSvg(text: String): String? =
    runCatching {
        if (!qrLibAvailable()) return null
        val factory: dynamic = js("window.qrcode")
        val qr = factory(0, "M")
        qr.addData(text)
        qr.make()
        qr.createSvgTag(js("({cellSize: 4, margin: 0, scalable: true})")) as String
    }.getOrNull()

// ---------- Loyalty section (side drawer, under the identity header) ----------

/** True when [qr] is unparseable or its expiry lies in the past. */
private fun qrExpired(qr: GuestApi.LoyaltyQr): Boolean {
    val expiresMs = Date(qr.expiresAt).getTime()
    return expiresMs.isNaN() || expiresMs <= Date.now()
}

/**
 * Loyalty card shown at the top of the burger drawer, right under the venue
 * identity header.
 *
 * Two states, both living in the same card:
 * - not signed in: a blurred teaser QR with a lock, the program pitch and the
 *   Google/Apple sign-in buttons (bonuses require an explicit OAuth account;
 *   the silent anon likes/ratings session never participates);
 * - signed in: the cashback balance + rate, and the guest's permanent QR/6-digit
 *   code the waiter scans or types when closing the bill. The code never
 *   rotates. This composable mounts only while the drawer is open, so the code
 *   is fetched on drawer open — never on page load — and [qr] is hoisted so
 *   reopening the drawer reuses the already-loaded code.
 *
 * [signedIn] is hoisted so a 403 LOYALTY_AUTH_REQUIRED seen anywhere (balance
 * load, code issue) flips the section back to the teaser.
 */
@Composable
internal fun LoyaltySection(
    restaurant: PublicRestaurantDto,
    lang: String,
    signedIn: Boolean,
    info: GuestApi.LoyaltyInfo?,
    qr: GuestApi.LoyaltyQr?,
    onInfo: (GuestApi.LoyaltyInfo?) -> Unit,
    onQr: (GuestApi.LoyaltyQr?) -> Unit,
    onAuthChange: (Boolean) -> Unit,
) {
    val strings = tenantStrings(lang)
    val scope = rememberCoroutineScope()

    var signInError by remember { mutableStateOf(false) }
    // null = still loading the CDN script, true/false = load outcome.
    var qrLibOk by remember { mutableStateOf<Boolean?>(null) }

    LaunchedEffect(Unit) { qrLibOk = loadQrLib().await() }

    /** After a successful OAuth sign-in: swap to the card and load the balance. */
    fun signedInNow() {
        signInError = false
        onAuthChange(true)
        scope.launch {
            GuestApi.loyalty(restaurant.slug)?.let(onInfo)
            // A 403 LOYALTY_AUTH_REQUIRED (stale token) drops the OAuth flag.
            if (!GuestSession.isOAuth) onAuthChange(false)
        }
    }

    // A "loyalty card": Surface base (dark-theme aware) under an accent-tinted
    // gradient wash, accent-tinted hairline + glow, and a faded QR-dot motif in
    // the corner. Same silhouette for both states so sign-in swaps in place.
    Column(
        Modifier
            .fillMaxWidth()
            .backgroundColor(Brand.Surface)
            .borderRadius(18.px)
            .padding(16.px)
            .margin(bottom = 14.px)
            .styleModifier {
                property("position", "relative")
                property("overflow", "hidden")
                // The drawer is a scrollable flex column: without this the card gets
                // squashed to a sliver when the drawer content overflows the viewport.
                property("flex-shrink", "0")
                property(
                    "background-image",
                    "linear-gradient(150deg," +
                        "color-mix(in srgb,var(--qk-accent,#C4573A) 14%,transparent) 0%," +
                        "transparent 46%," +
                        "color-mix(in srgb,var(--qk-accent,#C4573A) 8%,transparent) 100%)",
                )
                property("border", "1px solid color-mix(in srgb,var(--qk-accent,#C4573A) 24%,transparent)")
                property(
                    "box-shadow",
                    "0 6px 18px color-mix(in srgb,var(--qk-accent,#C4573A) 14%,transparent)",
                )
            },
    ) {
        // Decorative QR-dot motif fading out of the top-right corner.
        Div(
            attrs = {
                style {
                    property("position", "absolute")
                    property("top", "10px")
                    property("right", "12px")
                    property("width", "38px")
                    property("height", "30px")
                    property("pointer-events", "none")
                    property("opacity", "0.35")
                    property(
                        "background-image",
                        "radial-gradient(color-mix(in srgb,var(--qk-accent,#C4573A) 85%,transparent) 2px," +
                            "transparent 2.4px)",
                    )
                    property("background-size", "8px 8px")
                    property("-webkit-mask-image", "linear-gradient(215deg,#000,transparent 90%)")
                    property("mask-image", "linear-gradient(215deg,#000,transparent 90%)")
                }
            },
        )

        if (!signedIn) {
            Row(
                Modifier.fillMaxWidth().gap(12.px),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                LoyaltyQrTeaser(qrLibOk)
                Column(Modifier.flexGrow(1).minWidth(0.px)) {
                    SpanText(
                        strings.loyaltySectionTitle,
                        Modifier
                            .fontSize(18.px)
                            .fontWeight(FontWeight.Bold)
                            .color(Brand.Charcoal)
                            .styleModifier { property("line-height", "1.2") }
                            .attrsModifier { classes("qk-display") },
                    )
                    SpanText(
                        strings.loyaltySectionDescription,
                        Modifier
                            .fontSize(12.px)
                            .color(Brand.TextSecondary)
                            .margin(top = 6.px)
                            .styleModifier { property("line-height", "1.5") },
                    )
                }
            }

            LoyaltySignInButtons(
                strings = strings,
                lang = lang,
                error = signInError,
                onSignedIn = { signedInNow() },
            )
        } else {
            // Balance as the hero number, cashback rate as a small pill beside it.
            SpanText(
                strings.loyaltyBalanceLabel,
                Modifier
                    .fontSize(11.px)
                    .fontWeight(FontWeight.SemiBold)
                    .color(Brand.TextSecondary)
                    .styleModifier {
                        property("text-transform", "uppercase")
                        property("letter-spacing", "0.06em")
                    },
            )
            Row(
                Modifier.fillMaxWidth().margin(top = 2.px).gap(10.px),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically,
            ) {
                SpanText(
                    info?.let { formatMoneyMinor(it.balanceMinor, it.currency) } ?: "…",
                    Modifier
                        .fontSize(30.px)
                        .fontWeight(FontWeight.Bold)
                        .styleModifier {
                            property("color", "var(--qk-accent)")
                            property("line-height", "1.15")
                        }
                        .attrsModifier { classes("qk-display") },
                )
                Box(
                    Modifier
                        .borderRadius(Brand.RadiusChip.px)
                        .padding(topBottom = 4.px, leftRight = 10.px)
                        .attrsModifier { classes("qk-accent-soft") },
                ) {
                    SpanText(
                        strings.loyaltyCashbackRate(info?.ratePercent ?: restaurant.loyaltyRatePercent),
                        Modifier
                            .fontSize(11.px)
                            .fontWeight(FontWeight.SemiBold)
                            .styleModifier { property("color", "var(--qk-accent)") }
                            .attrsModifier { classes("qk-nowrap") },
                    )
                }
            }

            // Accent-tinted hairline between the balance and the live code.
            Box(
                Modifier
                    .fillMaxWidth()
                    .height(1.px)
                    .margin(top = 14.px)
                    .styleModifier {
                        property("background", "color-mix(in srgb,var(--qk-accent,#C4573A) 20%,transparent)")
                    },
            )

            LoyaltyLiveCode(
                restaurant = restaurant,
                strings = strings,
                qrLibOk = qrLibOk,
                qr = qr,
                onQr = onQr,
                onAuthRequired = { onAuthChange(false) },
            )

            // Sign out: revokes the SSO cookie server-side too — otherwise the
            // silent session exchange would immediately log the guest back in.
            Row(
                Modifier.fillMaxWidth().margin(top = 12.px),
                horizontalArrangement = Arrangement.Center,
            ) {
                SpanText(
                    menuT(lang).signOut,
                    Modifier
                        .fontSize(12.px)
                        .color(Brand.TextSecondary)
                        .cursor(Cursor.Pointer)
                        .attrsModifier { classes("qk-press"); attr("role", "button") }
                        .styleModifier { property("text-decoration", "underline dotted") }
                        .onClick {
                            scope.launch {
                                GuestApi.sessionLogout()
                                onQr(null)
                                onInfo(null)
                                onAuthChange(false)
                            }
                        },
                )
            }
        }
    }
}

/**
 * The guest's permanent QR/6-digit loyalty code, shown inline in the signed-in
 * section: renders the QR (`QRKL:{code}`) with a hint beneath. The code is
 * issued once per (guest, venue) and never rotates — the backend returns the
 * same code on every request, so there is no expiry countdown and no manual
 * re-issue; the only chip left is the retry after a failed load.
 *
 * [qr] is hoisted to survive drawer close/reopen: on mount a code is fetched
 * only when there is none yet (or a legacy short-lived one has expired).
 */
@Composable
private fun LoyaltyLiveCode(
    restaurant: PublicRestaurantDto,
    strings: MenuStrings,
    qrLibOk: Boolean?,
    qr: GuestApi.LoyaltyQr?,
    onQr: (GuestApi.LoyaltyQr?) -> Unit,
    onAuthRequired: () -> Unit,
) {
    val scope = rememberCoroutineScope()

    var qrBusy by remember { mutableStateOf(false) }
    var qrFailed by remember { mutableStateOf(false) }

    fun issueCode() {
        if (qrBusy) return
        qrBusy = true
        qrFailed = false
        scope.launch {
            val issued = GuestApi.loyaltyQr(restaurant.slug)
            qrBusy = false
            when {
                issued != null -> onQr(issued)
                // 403 LOYALTY_AUTH_REQUIRED or no token: fall back to sign-in.
                !GuestSession.isOAuth -> onAuthRequired()
                else -> qrFailed = true
            }
        }
    }

    // On drawer open: fetch the permanent code once; re-fetch only replaces a
    // legacy short-lived code that has already lapsed.
    LaunchedEffect(Unit) {
        if (qr == null || qrExpired(qr)) issueCode()
    }

    Column(
        Modifier.fillMaxWidth().margin(top = 14.px),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        val activeQr = qr
        if (activeQr == null) {
            if (qrFailed) {
                SpanText(
                    strings.loyaltyLoadFailed,
                    Modifier.fillMaxWidth().fontSize(12.px).color(Brand.CoralText)
                        .styleModifier { property("text-align", "center") },
                )
            } else {
                // First issue in flight: a QR-sized skeleton keeps the layout stable.
                Box(
                    Modifier.size(140.px).borderRadius(14.px)
                        .attrsModifier { classes("qk-skeleton") },
                )
            }
        } else {
            val svg = remember(activeQr.code, qrLibOk) { qrSvg("QRKL:${activeQr.code}") }

            if (svg != null) {
                // White quiet zone around the QR so dark themes stay scannable.
                Div(
                    attrs = {
                        style {
                            property("width", "140px")
                            property("height", "140px")
                            property("padding", "8px")
                            property("box-sizing", "border-box")
                            property("background", "#FFFFFF")
                            property("border-radius", "14px")
                            property("border", "1px solid #ECE8E2")
                        }
                        ref { el ->
                            el.innerHTML = svg
                            val child = el.firstElementChild
                            child?.setAttribute("width", "100%")
                            child?.setAttribute("height", "100%")
                            onDispose { el.innerHTML = "" }
                        }
                    },
                )
            } else if (qrLibOk == false) {
                // Script failed to load: show a short notice (the code is scan-only now).
                SpanText(
                    strings.loyaltyQrUnavailable,
                    Modifier.fontSize(12.px).color(Brand.TextSecondary)
                        .styleModifier { property("text-align", "center") },
                )
            }

            SpanText(
                strings.loyaltyCodeHint,
                Modifier.fontSize(12.px).color(Brand.TextSecondary).margin(top = 6.px)
                    .styleModifier { property("text-align", "center") },
            )
        }

        // Retry chip, only after a failed load — the code itself is permanent,
        // so there is nothing to manually refresh once it is on screen.
        if (activeQr == null && qrFailed) {
            Row(
                Modifier
                    .margin(top = 10.px)
                    .borderRadius(Brand.RadiusChip.px)
                    .padding(topBottom = 7.px, leftRight = 14.px)
                    .cursor(Cursor.Pointer)
                    .gap(6.px)
                    .attrsModifier {
                        classes("qk-press", "qk-accent-soft")
                        attr("role", "button")
                        attr("aria-label", strings.loyaltyRefreshCode)
                    }
                    .onClick { issueCode() },
                horizontalArrangement = Arrangement.Center,
                verticalAlignment = Alignment.CenterVertically,
            ) {
                InlineIcon(MenuIcons.Refresh, 13, "var(--qk-accent)")
                SpanText(
                    if (qrBusy) "…" else strings.loyaltyRefreshCode,
                    Modifier
                        .fontSize(12.px)
                        .fontWeight(FontWeight.SemiBold)
                        .styleModifier { property("color", "var(--qk-accent)") },
                )
            }
        }
    }
}

/**
 * Decorative blurred QR with a lock: a teaser of the personal code the guest
 * gets after signing in. Renders a real (fixed-content) QR when the CDN lib is
 * up, otherwise a blurred checkerboard that reads the same at 6px of blur.
 */
@Composable
private fun LoyaltyQrTeaser(qrLibOk: Boolean?) {
    val svg = remember(qrLibOk) { if (qrLibOk == true) qrSvg("QRKONTAQ") else null }
    Box(
        Modifier
            .size(104.px)
            .minWidth(104.px)
            .borderRadius(14.px)
            .border(1.px, LineStyle.Solid, Brand.Divider)
            .overflow(Overflow.Hidden)
            .styleModifier {
                // Raw `background` (not background-color) so the dark theme keeps the
                // white quiet zone, matching the real QR card shown once signed in.
                property("background", "#FFFFFF")
                property("box-shadow", "0 4px 12px rgba(31,36,33,.14)")
            }
            .attrsModifier { attr("aria-hidden", "true") },
        contentAlignment = Alignment.Center,
    ) {
        if (svg != null) {
            Div(
                attrs = {
                    style {
                        property("width", "82px")
                        property("height", "82px")
                        property("filter", "blur(6px)")
                        property("opacity", "0.55")
                        property("pointer-events", "none")
                    }
                    ref { el ->
                        el.innerHTML = svg
                        val child = el.firstElementChild
                        child?.setAttribute("width", "100%")
                        child?.setAttribute("height", "100%")
                        onDispose { el.innerHTML = "" }
                    }
                },
            )
        } else {
            // CDN missing (or still loading): a blurred grid of squares stands in.
            Div(
                attrs = {
                    style {
                        property("width", "82px")
                        property("height", "82px")
                        property("filter", "blur(6px)")
                        property("opacity", "0.45")
                        property("pointer-events", "none")
                        property("background", "repeating-conic-gradient(#1F2421 0% 25%, #FFFFFF 0% 50%)")
                        property("background-size", "16px 16px")
                    }
                },
            )
        }
        Box(
            Modifier
                .size(30.px)
                .borderRadius(Brand.RadiusChip.px)
                .attrsModifier { classes("qk-glass-strong") }
                .boxShadow(offsetY = 2.px, blurRadius = 8.px, color = Color.rgba(31, 36, 33, 0.18f)),
            contentAlignment = Alignment.Center,
        ) {
            InlineIcon(MenuIcons.Lock, 14, "#1F2421", extraClass = "qk-ink")
        }
    }
}

/**
 * Google + Apple sign-in buttons for the loyalty section: bonuses require an
 * explicit Google/Apple account. Both buttons open the central auth popup
 * (auth.qrkontaq.com) — running the OAuth flows on the venue subdomain itself
 * is impossible (origin_mismatch: wildcard origins can't be registered).
 */
@Composable
private fun LoyaltySignInButtons(
    strings: MenuStrings,
    lang: String,
    error: Boolean,
    onSignedIn: () -> Unit,
) {
    androidx.compose.runtime.DisposableEffect(Unit) {
        val dispose = listenForGuestAuth { onSignedIn() }
        onDispose { dispose() }
    }

    Column(
        Modifier.fillMaxWidth().margin(top = 14.px),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        // Google
        Row(
            Modifier
                .fillMaxWidth()
                .styleModifier { property("background-color", "#FFFFFF") }
                .border(1.px, LineStyle.Solid, Brand.Divider)
                .borderRadius(11.px)
                .padding(topBottom = 10.px)
                .cursor(Cursor.Pointer)
                .attrsModifier { classes("qk-press"); attr("role", "button") }
                .onClick { openGuestAuthPopup("google", lang) },
            horizontalArrangement = Arrangement.Center,
            verticalAlignment = Alignment.CenterVertically,
        ) {
            InlineIcon(MenuIcons.GoogleLogo, 16, "#1F2421")
            SpanText(
                strings.loyaltySignInGoogle,
                Modifier
                    .fontSize(13.px)
                    .fontWeight(FontWeight.SemiBold)
                    .color(Brand.Charcoal)
                    .margin(left = 8.px),
            )
        }

        // Apple
        Row(
            Modifier
                .fillMaxWidth()
                .backgroundColor(Brand.Charcoal)
                .borderRadius(11.px)
                .padding(topBottom = 10.px)
                .margin(top = 8.px)
                .cursor(Cursor.Pointer)
                .attrsModifier { classes("qk-press"); attr("role", "button") }
                .onClick { openGuestAuthPopup("apple", lang) },
            horizontalArrangement = Arrangement.Center,
            verticalAlignment = Alignment.CenterVertically,
        ) {
            InlineIcon(MenuIcons.AppleLogo, 16, "#FFFFFF")
            SpanText(
                strings.loyaltySignInApple,
                Modifier
                    .fontSize(13.px)
                    .fontWeight(FontWeight.SemiBold)
                    .color(Brand.Surface)
                    .margin(left = 8.px),
            )
        }

        if (error) {
            SpanText(
                strings.loyaltySignInFailed,
                Modifier.fontSize(12.px).color(Brand.CoralText).margin(top = 10.px)
                    .styleModifier { property("text-align", "center") },
            )
        }
    }
}
